import { Injectable } from '@angular/core';
import { pickRecordingType, SPEECH_BITS_PER_SECOND } from './recording-format';

/**
 * How often the recorder hands over what it has written. A session cut short by a
 * crash still leaves what was said up to that point, rather than one chunk that
 * was never delivered.
 */
const RECORDING_TIMESLICE_MS = 5000;

/**
 * The type of a recording made of more than one file, which is no single
 * container; each file's own type is in `partTypes`.
 */
export const JOINED_RECORDING_TYPE = 'application/octet-stream';

/**
 * A run of consecutive files in a recording whose words belong together: `parts`
 * files, and the stored note text they follow, or null to add them at the end.
 */
export interface RecordingSection {
  parts: number;
  before: string | null;
}

/** A finished recording, as it comes off the microphone. */
export interface CapturedAudio {
  audio: Blob;
  mimeType: string;
  seconds: number;
  /**
   * The byte length of each file `audio` is made of, in the order they were
   * recorded, where it is made of more than one. Absent for a single file.
   */
  partLengths?: number[];
  /** The type of each file `audio` is made of, in the same order as `partLengths`. */
  partTypes?: string[];
  /**
   * Where the words of each run of files belong in the note, in order. Absent for a
   * recording whose words all go at the end.
   */
  sections?: RecordingSection[];
}

/**
 * Join what a later pass recorded onto what the earlier passes did. A rep who
 * pauses to think and resumes has said one thing, not two, and the recogniser
 * should be handed the whole of it. Each pass stays a whole file of its own, with
 * its length and type recorded, because a decoder reads one file at a time; the
 * joined recording itself carries {@link JOINED_RECORDING_TYPE}.
 */
export function joinCaptured(
  earlier: CapturedAudio | null | undefined,
  later: CapturedAudio | null | undefined,
): CapturedAudio | null {
  if (!earlier) return later ?? null;
  if (!later) return earlier;
  return {
    audio: new Blob([earlier.audio, later.audio], { type: JOINED_RECORDING_TYPE }),
    mimeType: JOINED_RECORDING_TYPE,
    seconds: earlier.seconds + later.seconds,
    partLengths: [...partLengthsOf(earlier.audio, earlier.partLengths), ...partLengthsOf(later.audio, later.partLengths)],
    partTypes: [...partTypesOf(earlier), ...partTypesOf(later)],
    sections: [...sectionsOf(earlier), ...sectionsOf(later)],
  };
}

/** `captured` as one run of files whose words follow `before` in the stored note. */
export function placedBefore(captured: CapturedAudio, before: string | null): CapturedAudio {
  return { ...captured, sections: [{ parts: partLengthsOf(captured.audio, captured.partLengths).length, before }] };
}

/**
 * The files of a recording grouped by where their words belong: one group per
 * section, or a single group of every file, added at the end, where the recording
 * has no sections. Files beyond the sections' count are added at the end as well.
 */
export function recordingSections(
  audio: Blob,
  partLengths?: readonly number[] | null,
  partTypes?: readonly string[] | null,
  sections?: readonly RecordingSection[] | null,
): { parts: Blob[]; before: string | null }[] {
  const parts = recordingParts(audio, partLengths, partTypes);
  const groups: { parts: Blob[]; before: string | null }[] = [];
  let next = 0;
  for (const section of sections ?? []) {
    groups.push({ parts: parts.slice(next, next + section.parts), before: section.before });
    next += section.parts;
  }
  if (next < parts.length) {
    groups.push({ parts: parts.slice(next), before: null });
  }
  return groups.filter((group) => group.parts.length);
}

/**
 * The files a recording is made of, in order: one per length in `partLengths`,
 * typed from `partTypes` where given, or the whole recording where there are none.
 * A length that runs past the end of the recording is cut short, and nothing empty
 * is returned.
 */
export function recordingParts(
  audio: Blob,
  partLengths?: readonly number[] | null,
  partTypes?: readonly string[] | null,
): Blob[] {
  const parts: Blob[] = [];
  let start = 0;
  for (const [index, length] of partLengthsOf(audio, partLengths).entries()) {
    const end = Math.min(audio.size, start + Math.max(0, length));
    if (end > start) {
      parts.push(audio.slice(start, end, partTypes?.[index] ?? audio.type));
    }
    start = end;
  }
  if (start < audio.size) {
    parts.push(audio.slice(start, audio.size, audio.type));
  }
  return parts;
}

function partLengthsOf(audio: Blob, partLengths?: readonly number[] | null): readonly number[] {
  return partLengths?.length ? partLengths : [audio.size];
}

function partTypesOf(captured: CapturedAudio): readonly string[] {
  return captured.partTypes?.length ? captured.partTypes : [captured.mimeType];
}

function sectionsOf(captured: CapturedAudio): readonly RecordingSection[] {
  return captured.sections?.length
    ? captured.sections
    : [{ parts: partLengthsOf(captured.audio, captured.partLengths).length, before: null }];
}

/** One stretch of recording, from start to stop, with what it has written so far. */
interface RecordingPass {
  recorder: MediaRecorder;
  chunks: Blob[];
  startedAt: number;
  /** Settles once the platform has begun recording (the recorder's `start` event). */
  started: Promise<void>;
}

/**
 * Writes down what the rep says, for a session whose words cannot be read now. It
 * records the stream it is handed, the microphone live recognition is also fed
 * from, so moving from one to the other opens nothing new and loses nothing to a
 * second prompt.
 */
@Injectable({ providedIn: 'root' })
export class AudioRecorder {
  #pass: RecordingPass | null = null;

  /** Whether this platform records at all: the API, and a container it will write. */
  public get isAvailable(): boolean {
    return typeof MediaRecorder !== 'undefined'
      && !!navigator?.mediaDevices?.getUserMedia
      && !!pickRecordingType((type) => MediaRecorder.isTypeSupported(type));
  }

  public get isRecording(): boolean {
    return this.#pass?.recorder.state === 'recording';
  }

  /**
   * Start writing down what `stream` carries, unless a pass is already running. The
   * stream belongs to the caller, which opens and closes the microphone; stopping
   * the recording leaves its tracks alone. Throws, at once, where this device
   * records no format the recogniser can read.
   *
   * @returns Settles once the platform has begun recording, which is when what is
   *   said starts to be kept.
   */
  public start(stream: MediaStream): Promise<void> {
    if (this.#pass) {
      return this.#pass.started;
    }
    const mimeType = pickRecordingType((type) => MediaRecorder.isTypeSupported(type));
    if (!mimeType) {
      throw new Error('This device records no audio format the recogniser can read.');
    }
    const recorder = new MediaRecorder(stream, {
      mimeType,
      audioBitsPerSecond: SPEECH_BITS_PER_SECOND,
    });
    const started = new Promise<void>((resolve) => {
      recorder.onstart = () => resolve();
    });
    const pass: RecordingPass = { recorder, chunks: [], startedAt: Date.now(), started };
    recorder.ondataavailable = (event) => {
      if (event.data?.size) {
        pass.chunks.push(event.data);
      }
    };
    this.#pass = pass;
    recorder.start(RECORDING_TIMESLICE_MS);
    return started;
  }

  /**
   * Stop and hand back what was said. The pass is let go of at once, so a new one
   * can start while this one is still delivering its last chunk.
   */
  public async stop(): Promise<CapturedAudio | null> {
    const pass = this.#pass;
    if (!pass) {
      return null;
    }
    this.#pass = null;
    if (pass.recorder.state !== 'inactive') {
      const finished = new Promise<void>((resolve) => {
        pass.recorder.onstop = () => resolve();
      });
      pass.recorder.stop();
      await finished;
    }
    if (!pass.chunks.length) {
      return null;
    }
    const mimeType = pass.recorder.mimeType || 'audio/webm';
    return {
      audio: new Blob(pass.chunks, { type: mimeType }),
      mimeType,
      seconds: Math.round((Date.now() - pass.startedAt) / 1000),
    };
  }
}
